import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { ConfigurationService, Locale, LocaleService, OptimService } from '@ciam/library';
import { BehaviorSubject, combineLatest, take } from 'rxjs';
import { LoginInfoService } from '../../services/login-info/login-info.service';
import { SharedService } from 'src/app/shared/services/shared-service/shared.service';
import { HostContextService } from '../../services/host-context/host-context.service';
import { GdlService } from 'src/app/shared/services/gdl-service/gdl.service';

type Options = 'loading' | 'enabled' | 'disabled';

@Component({
  selector: 'wpro-side-bar',
  templateUrl: './sidebar-nav.component.html',
  standalone: false
})

export class SidebarNavComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  //Sidebar variables
  isOverviewEnabled: Options;
  isFdmEnabled: Options;
  isClearanceProfileEnabled: Options;
  isWpdmDecoupled: boolean;
  isWpapDecoupled: boolean;
  private _host = `https://www.fedex.com`;
  private hasCustomHost = false;
  get host(): string {
    return this._host;
  }
  set host(value: string) {
    this._host = value;
    this.hasCustomHost = !!value;
  }
  locale$: BehaviorSubject<Locale>;
  activeLink: 'loginSecurity' | 'contactInformation' | null = null;

  // Navigation URLs - built based on Polaris/dotcom mode
  navUrls = {
    profileOverview: '',
    loginSecurity: '',
    contactInfo: '',
    deliveryManager: '',
    shippingAdmin: '',
    accountManagement: '',
    legacyAccountManagement: '',
    clearanceProfile: ''
  };
  preferencesUrl: string = '';

  // Polaris shell uses path-based routing, dotcom uses hash-based
  get isPolaris(): boolean {
    return this.hostContext.isPolaris;
  }

  // In Polaris shell, build links on the active browser host to avoid
  // cross-environment redirects when config host differs from shell host.
  private get navHost(): string {
    return this.isPolaris ? window.location.origin : this.host;
  }

  constructor(
    private configService: ConfigurationService,
    private localeService: LocaleService,
    private loginInfoService: LoginInfoService,
    private optimService: OptimService,
    public sharedService: SharedService,
    private router: Router,
    private hostContext: HostContextService,
    private gdlService: GdlService,
  ) {
    this.locale$ = new BehaviorSubject<Locale>(this.localeService.solveLocale()); //initialize with current locale
    this.localeService.watch$().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(this.locale$); //subscribe to any new locale changes
  }

  ngOnInit(): void {
    this.configService.get$('env.host').pipe(takeUntilDestroyed(this.destroyRef)).subscribe((host) => {
      if (!this.hasCustomHost) {
        this.host = `https://${host}`;
      }
      this.buildNavUrls();
      this.checkForPreference();
    });

    // Rebuild URLs when locale changes
    this.locale$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.buildNavUrls();
      this.checkForPreference();
    });

    //highlight sidebar active link on initial load
    this.updateActiveLink();

    // Listen to route changes to update active link dynamically
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.updateActiveLink();
    });

    this.prepareSideBar();
  }

  /**
   * Build navigation URLs based on Polaris/dotcom mode
   */
  buildNavUrls(): void {
    const iso = this.locale$.getValue().iso;
    const posix = this.locale$.getValue().posix;
    const country = this.locale$.getValue().country;

    // Shipping Admin always resolves to dotcom (www.*), even when the app is
    // mounted under a my.* host (e.g. my.fedex.com, mytest.fedex.com).
    const shippingAdmin = `${this.toDotcomHost(this.host)}/apps/shipadmin/?wpro=true`;

    if (this.isPolaris) {
      // Polaris uses path-based routing (no hash). The prefix (e.g. `/shippingplus`
      // on L3, empty on L6) is derived from the current URL so links to sibling
      // apps follow the same host convention without per-environment builds.
      const base = `${this.navHost}${this.hostContext.polarisPathPrefix}/${iso}/profile`;
      this.navUrls = {
        profileOverview: `${base}/overview`,
        loginSecurity: `${base}/info/login`,
        contactInfo: `${base}/info/contact`,
        deliveryManager: `${base}/delivery-management/status`,
        shippingAdmin,
        accountManagement: `${base}/accounts-payment/manage`,
        legacyAccountManagement: '',
        clearanceProfile: `${base}/clearance-profile`
      };
    } else {
      // Dotcom uses hash-based routing
      const isWpdmDecoupled = !!this.configService.select<boolean>('decoupleWpdmLinkFlag');
      const deliveryManager = isWpdmDecoupled
        ? `${this.navHost}/apps/myprofile/deliverymanager/?locale=${posix}&cntry_code=${country}&wpro=true`
        : `${this.navHost}/profile-delivery-manager/${iso}/`;

      this.navUrls = {
        profileOverview: `${this.navHost}/profile-overview/${iso}/`,
        loginSecurity: `${this.navHost}/profile/${iso}/#/login`,
        contactInfo: `${this.navHost}/profile/${iso}/#/contact`,
        deliveryManager,
        shippingAdmin,
        accountManagement: `${this.navHost}/profile-accounts-payment/${iso}/`,
        legacyAccountManagement: `${this.navHost}/apps/myprofile/accountmanagement/?locale=${posix}&cntry_code=${country}&wpro=true`,
        clearanceProfile: `${this.navHost}/clearance-profile/${iso}/`
      };
    }
  }

  private updateActiveLink(): void {
    if (window.location.href.includes('/contact')) {
      this.activeLink = 'contactInformation';
    } else {
      this.activeLink = 'loginSecurity';
    }
  }

  /**
   * Sets vars for side bar items
   */
  prepareSideBar(): void {
    this.checkForOverview();
    this.checkForFdm();
    this.checkForWpap();
    this.checkForPreference();
    this.checkForClearanceProfile();
  }

  checkForOverview(): void {
    this.isOverviewEnabled = 'loading';
    this.optimService.decide$('wport_audience').subscribe((result) => {
      this.isOverviewEnabled = result.enabled ? 'enabled' : 'disabled';
    });
  }

  checkForFdm(): void {
    this.isFdmEnabled = 'loading';
    this.configService.get$<boolean>('decoupleWpdmLinkFlag').subscribe((flag) => {
      this.isWpdmDecoupled = flag;
    });
    combineLatest([this.locale$, this.loginInfoService.updateRecipients()]).subscribe((results) => {
      const customerType = results[1].output.ctype;
      if (!this.isWpdmDecoupled && (results[0].country === 'us') && (customerType === '1' || customerType === '2')) {
        this.isFdmEnabled = 'enabled';
      } else if (this.isWpdmDecoupled && (results[0].country === 'us') && (customerType === '1' || customerType === '2')) {
        this.isFdmEnabled = 'enabled';
      } else {
        this.isFdmEnabled = 'disabled';
      }
    });
  }

  checkForWpap(): void {
    this.optimService.decide$('wpap_sidebar_decouple').pipe(take(1)).subscribe((result) => {
      this.isWpapDecoupled = result.enabled;
    });
  }

  checkForClearanceProfile(): void {
    this.isClearanceProfileEnabled = 'loading';
    combineLatest([
      this.optimService.decide$('clearance_profile').pipe(take(1)),
      this.sharedService.userInfoResponse$
    ]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(([result, userInfo]) => {
      const countryCode = userInfo?.output?.userProfile?.userProfileAddress?.address?.countryCode?.toUpperCase();
      this.isClearanceProfileEnabled = result.enabled && ['US', 'CA'].includes(countryCode) ? 'enabled' : 'disabled';
    });
  }

  recordClearanceProfileClick(): void {
     this.gdlService.triggerGdlLogEvent('interaction', 'click', {
      name: 'wpro-clearance-profile',
      value: 'clearance-profile-selected'
    });
  }

  checkForPreference(): void {
    const iso = this.locale$.getValue().iso;
    const posix = this.locale$.getValue().posix;
    const country = this.locale$.getValue().country;
    const currentHost = this.isPolaris ? window.location.origin : this.host;

    this.optimService.decide$('wpap_newpreferences_audience').pipe(take(1)).subscribe((result) => {
      if (result.enabled === true) {
        this.preferencesUrl = this.isPolaris
          ? `${currentHost}${this.hostContext.polarisPathPrefix}/${iso}/profile/accounts-payment/preferences`
          : `${currentHost}/profile-accounts-payment/${iso}/#/preferences`;
      } else {
        // Legacy URL used for both Polaris and Dotcom. Preferences always
        // resolves to dotcom (www.*) here, even when the app is mounted under a
        // my.* host.
        this.preferencesUrl = `${this.toDotcomHost(currentHost)}/wprl/preferences/?locale=${posix}&cntry_code=${country}&pkgPreference=true&wpro=true`;
      }
    });
  }

  setActive(link: 'loginSecurity' | 'contactInformation', event: Event, url: string): void {
    // Prevent default navigation
    event.preventDefault();

    if(link == 'loginSecurity'){
      this.sharedService.isLoginMenuClicked = true;
    }

    // Check if ANY edit screen is active (Login or Contact)
    const hasUnsavedChanges = this.sharedService.isEditScreen || this.sharedService.isEditContactScreen;

    if (hasUnsavedChanges) {
      // Trigger the popup flow - the components will handle navigation
      this.sharedService.selectedExternalRoute.next(url);
    } else {
      // No unsaved changes, navigate directly
      this.activeLink = link;

      // Polaris: use Angular router for internal navigation
      if (this.isPolaris) {
        this.router.navigate([link === 'loginSecurity' ? '/login' : '/contact']);
      } else {
        // Dotcom: use full URL with hash routing
        window.location.href = url;
      }
    }
  }

   /**
   * Converts a FedEx `my.*` host to its dotcom (`www.*`) equivalent so that the
   * Shipping Admin and Preferences links always resolve to dotcom, even when the
   * app is mounted under a my.* host. Only the leading `my` label of the hostname
   * (right after the scheme) is rewritten:
   *
   *   https://my.fedex.com                -> https://www.fedex.com
   *   https://my.fedex.com.cn             -> https://www.fedex.com.cn
   *   https://mytest.fedex.com            -> https://wwwtest.fedex.com
   *   https://mydrt.idev.fedex.com        -> https://wwwdrt.idev.fedex.com
   *   https://mystress.dmz.idev.fedex.com -> https://wwwstress.dmz.idev.fedex.com
   *
   * Non-`my` hosts are returned unchanged.
   */
  private toDotcomHost(host: string): string {
    return host.replace(/^(https?:\/\/)my/i, '$1www');
  }
}
